import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button, Fade, Grid, Stack, Typography } from '@mui/material';
import { useConfirm } from 'material-ui-confirm';
import AddIcon from '@mui/icons-material/Add';

import {
  IAdminRemoveSubTopicResouces,
  IAdminRemoveSubTopicVideoLink,
  ISubTopicContent,
  ISubTopicFAQ,
  ISubTopicReview,
  ISubTopicVideoLink,
  adminAddSubTopicContent,
  adminEditSubTopicContent,
  adminRemoveSubTopicContent,
  adminRemoveSubTopicFAQ,
  adminRemoveSubTopicResources,
  adminRemoveSubTopicReview,
  adminRemoveSubTopicVideoLink,
  getSubTopicState,
} from '../../../store/SubTopicSlice';
import { IResourceFile } from '../../../store/ResourcesSlice';
import { useAppSelector } from '../../../hooks';
import AddNewResouceModal from './modals/AddNewResouceModal';
import AddNewVideoLinkModal from './modals/AddNewVideoLinkModal';
import ResourceCard from './components/ResourceCard';
import VideoLinkCard from './components/VideoLinkCard';
import AddDetailModal from './components/ManageDetails/modals/AddDetailModal';
import ContentCard from './components/ContentCard';
import AddFAQModal from './modals/AddFAQModal';
import FAQCard from './components/FAQCard';
import AddReviewModal from './modals/AddReviewModal';
import ReviewCard from './components/ReviewCard';

interface IState {
  isAddResourceModalOpen: boolean;
  isAddVideoLinkModalOpen: boolean;
  isEditVideoLinkModalOpen: boolean;
  isContentModalOpen: boolean;
  isFAQModalOpen: boolean;
  isReviewModalOpen: boolean;
  selectedVideoLink?: ISubTopicVideoLink;
  selectedContent?: ISubTopicContent;
  selectedFAQ?: ISubTopicFAQ;
  selectedReview?: ISubTopicReview;
}

const initialState: IState = {
  isAddResourceModalOpen: false,
  isAddVideoLinkModalOpen: false,
  isEditVideoLinkModalOpen: false,
  isContentModalOpen: false,
  isFAQModalOpen: false,
  isReviewModalOpen: false,
  selectedVideoLink: undefined,
  selectedContent: undefined,
  selectedFAQ: undefined,
  selectedReview: undefined,
};

const LessonMaterials: React.FC = () => {
  const urlParams = useParams();
  const confirm = useConfirm();
  const dispatch = useDispatch();

  // Store
  const subTopicState = useAppSelector(getSubTopicState);

  // State
  const [state, setState] = useState(initialState);

  const { subTopicId } = urlParams;
  const subTopicData = subTopicState.subTopic;
  const subTopicResources =
    subTopicData && subTopicData.resources && subTopicData.resources.length
      ? subTopicData.resources
      : [];

  const subTopicVideoLinks =
    subTopicData && subTopicData.videoLinks && subTopicData.videoLinks.length
      ? subTopicData.videoLinks
      : [];

  const subTopicContents =
    subTopicData && subTopicData.contents && subTopicData.contents.length
      ? subTopicData.contents
      : [];

  const subTopicFAQs =
    subTopicData && subTopicData.FAQs && subTopicData.FAQs.length
      ? subTopicData.FAQs
      : [];

  const subTopicReviews =
    subTopicData && subTopicData.reviews && subTopicData.reviews.length
      ? subTopicData.reviews
      : [];

  const toggleAddNewResourceModal = () => {
    setState((prevState) => ({
      ...prevState,
      isAddResourceModalOpen: !prevState.isAddResourceModalOpen,
    }));
  };

  const toggleAddNewVideoLinkModal = () => {
    setState((prevState) => ({
      ...prevState,
      isAddVideoLinkModalOpen: !prevState.isAddVideoLinkModalOpen,
    }));
  };

  const handleDeleteResources = async (subTopicResource: IResourceFile) => {
    if (subTopicId) {
      confirm({
        title: 'Are you sure?',
        description: 'Do you want to delete this lesson material?',
      }).then(() => {
        const apiData: IAdminRemoveSubTopicResouces = {
          subTopicId,
          resourceId: subTopicResource.id,
        };

        dispatch(adminRemoveSubTopicResources(apiData));
      });
    }
  };

  const handleDeleteVideoLink = (subTopicVideoLink: ISubTopicVideoLink) => {
    if (subTopicId) {
      confirm({
        title: 'Are you sure?',
        description: 'Do you want to delete this video link?',
      }).then(() => {
        const apiData: IAdminRemoveSubTopicVideoLink = {
          subTopicId,
          videoLinkId: subTopicVideoLink._id,
        };

        dispatch(adminRemoveSubTopicVideoLink(apiData));
      });
    }
  };

  const handleDeleteContent = (subTopicContent: ISubTopicContent) => {
    if (subTopicId) {
      confirm({
        title: 'Are you sure?',
        description: 'Do you want to delete this content?',
      }).then(() => {
        dispatch(
          adminRemoveSubTopicContent({
            subTopicId,
            contentId: subTopicContent._id,
          })
        );
      });
    }
  };

  const handleDeleteFAQ = (subTopicFAQ: ISubTopicFAQ) => {
    if (subTopicId) {
      confirm({
        title: 'Are you sure?',
        description: 'Do you want to delete this FAQ?',
      }).then(() => {
        dispatch(
          adminRemoveSubTopicFAQ({
            subTopicId,
            FAQId: subTopicFAQ._id,
          })
        );
      });
    }
  };

  const handleDeleteReview = (subTopicReview: ISubTopicReview) => {
    if (subTopicId) {
      confirm({
        title: 'Are you sure?',
        description: 'Do you want to delete this Review?',
      }).then(() => {
        dispatch(
          adminRemoveSubTopicReview({
            subTopicId,
            reviewId: subTopicReview._id,
          })
        );
      });
    }
  };

  const openEditContentModal = (item: ISubTopicContent) => {
    setState((prevState) => ({
      ...prevState,
      isContentModalOpen: true,
      selectedContent: item,
    }));
  };

  const openEditVideoLinkModal = (item: ISubTopicVideoLink) => {
    setState((prevState) => ({
      ...prevState,
      isEditVideoLinkModalOpen: true,
      selectedVideoLink: item,
    }));
  };

  const openEditFAQModal = (item: ISubTopicFAQ) => {
    setState((prevState) => ({
      ...prevState,
      isFAQModalOpen: true,
      selectedFAQ: item,
    }));
  };

  const openEditReviewModal = (item: ISubTopicReview) => {
    setState((prevState) => ({
      ...prevState,
      isReviewModalOpen: true,
      selectedReview: item,
    }));
  };

  const closeEditVideoLinkModal = () => {
    setState((prevState) => ({
      ...prevState,
      isEditVideoLinkModalOpen: false,
      selectedVideoLink: undefined,
    }));
  };

  const toggleContentModal = (value: boolean) => {
    setState((prevState) => ({
      ...prevState,
      isContentModalOpen: value,
      selectedContent: undefined,
    }));
  };

  const toggleAddFAQModal = () => {
    setState((prevState) => ({
      ...prevState,
      isFAQModalOpen: !prevState.isFAQModalOpen,
      selectedFAQ: undefined,
    }));
  };

  const toggleAddReviewModal = () => {
    setState((prevState) => ({
      ...prevState,
      isReviewModalOpen: !prevState.isReviewModalOpen,
      selectedReview: undefined,
    }));
  };

  return (
    <Fade in>
      <div>
        <Stack
          direction="row"
          alignItems="center"
          style={{ marginBottom: '15px' }}
        >
          <Typography variant="h5">LESSON SLIDES</Typography>
          <Button
            color="primary"
            variant="text"
            startIcon={<AddIcon />}
            style={{ marginLeft: '10px' }}
            onClick={toggleAddNewResourceModal}
          >
            Add New Lesson Slide
          </Button>
        </Stack>
        <div style={{ marginBottom: '30px' }}>
          <Grid container spacing={2}>
            {subTopicResources.length
              ? subTopicResources.map((subTopicResource, index) => (
                  <Grid item xs={12} key={index}>
                    <ResourceCard
                      resource={subTopicResource}
                      onDelete={() => handleDeleteResources(subTopicResource)}
                    />
                  </Grid>
                ))
              : null}
          </Grid>
        </div>
        <Stack
          direction="row"
          alignItems="center"
          style={{ marginBottom: '15px' }}
        >
          <Typography variant="h5">VIDEO MATERIALS</Typography>
          <Button
            color="primary"
            variant="text"
            startIcon={<AddIcon />}
            style={{ marginLeft: '10px' }}
            onClick={toggleAddNewVideoLinkModal}
          >
            Add New Video Material
          </Button>
        </Stack>
        <div style={{ marginBottom: '30px' }}>
          <Grid container spacing={2}>
            {subTopicVideoLinks.length
              ? subTopicVideoLinks.map((subTopicVideoLink, index) => (
                  <Grid item xs={12} key={index}>
                    <VideoLinkCard
                      itemIndex={index}
                      listLength={subTopicVideoLinks.length}
                      data={subTopicVideoLink}
                      onDelete={() => handleDeleteVideoLink(subTopicVideoLink)}
                      onUpdate={() => openEditVideoLinkModal(subTopicVideoLink)}
                    />
                  </Grid>
                ))
              : null}
          </Grid>
        </div>

        {/* CONTENT SECTION */}
        <Stack
          direction="row"
          alignItems="center"
          style={{ marginBottom: '15px' }}
        >
          <Typography variant="h5">CONTENT</Typography>
          <Button
            color="primary"
            variant="text"
            startIcon={<AddIcon />}
            style={{ marginLeft: '10px' }}
            onClick={() => toggleContentModal(true)}
          >
            Add New Content
          </Button>
        </Stack>
        <div style={{ marginBottom: '30px' }}>
          <Grid container spacing={2}>
            {subTopicContents.length
              ? subTopicContents.map((subTopicContent, index) => (
                  <Grid item xs={12} key={index}>
                    <ContentCard
                      data={subTopicContent}
                      onDelete={() => handleDeleteContent(subTopicContent)}
                      onUpdate={() => openEditContentModal(subTopicContent)}
                    />
                  </Grid>
                ))
              : null}
          </Grid>
        </div>

        {/* FAQS SECTION */}
        <Stack
          direction="row"
          alignItems="center"
          style={{ marginBottom: '15px' }}
        >
          <Typography variant="h5">FAQs</Typography>
          <Button
            color="primary"
            variant="text"
            startIcon={<AddIcon />}
            style={{ marginLeft: '10px' }}
            onClick={() => toggleAddFAQModal()}
          >
            Add New Question
          </Button>
        </Stack>
        <div style={{ marginBottom: '30px' }}>
          <Grid container spacing={2}>
            {subTopicFAQs.length
              ? subTopicFAQs.map((subTopicFAQ, index) => (
                  <Grid item xs={12} key={index}>
                    <FAQCard
                      data={subTopicFAQ}
                      onDelete={() => handleDeleteFAQ(subTopicFAQ)}
                      onUpdate={() => openEditFAQModal(subTopicFAQ)}
                    />
                  </Grid>
                ))
              : null}
          </Grid>
        </div>

        {/* Review SECTION */}
        <Stack
          direction="row"
          alignItems="center"
          style={{ marginBottom: '15px' }}
        >
          <Typography variant="h5">Reviews</Typography>
          <Button
            color="primary"
            variant="text"
            startIcon={<AddIcon />}
            style={{ marginLeft: '10px' }}
            onClick={() => toggleAddReviewModal()}
          >
            Add New Review
          </Button>
        </Stack>
        <div style={{ marginBottom: '30px' }}>
          <Grid container spacing={2}>
            {subTopicReviews.length
              ? subTopicReviews.map((subTopicReview, index) => (
                  <Grid item xs={12} key={index}>
                    <ReviewCard
                      data={subTopicReview}
                      onDelete={() => handleDeleteReview(subTopicReview)}
                      onUpdate={() => openEditReviewModal(subTopicReview)}
                    />
                  </Grid>
                ))
              : null}
          </Grid>
        </div>

        {/* Modals */}
        <AddNewResouceModal
          open={state.isAddResourceModalOpen}
          onClose={toggleAddNewResourceModal}
        />
        <AddNewVideoLinkModal
          open={state.isAddVideoLinkModalOpen}
          onClose={toggleAddNewVideoLinkModal}
        />
        <AddNewVideoLinkModal
          editMode
          open={state.isEditVideoLinkModalOpen}
          onClose={closeEditVideoLinkModal}
          subTopicVideoLink={state.selectedVideoLink}
        />
        {state.isContentModalOpen && (
          <AddDetailModal
            open={state.isContentModalOpen}
            details={state.selectedContent?.details || ''}
            onClose={() => toggleContentModal(false)}
            updateDetails={(html) => {
              if (subTopicId && html) {
                if (state.selectedContent) {
                  dispatch(
                    adminEditSubTopicContent({
                      subTopicId,
                      contentId: state.selectedContent._id,
                      details: html,
                    })
                  );
                } else {
                  dispatch(
                    adminAddSubTopicContent({ subTopicId, details: html })
                  );
                }
              }
              toggleContentModal(false);
            }}
          />
        )}
        {state.isFAQModalOpen && (
          <AddFAQModal
            open={state.isFAQModalOpen}
            selectedFAQ={state.selectedFAQ}
            onClose={() => toggleAddFAQModal()}
          />
        )}
        {state.isReviewModalOpen && (
          <AddReviewModal
            open={state.isReviewModalOpen}
            selectedReview={state.selectedReview}
            onClose={() => toggleAddReviewModal()}
          />
        )}
      </div>
    </Fade>
  );
};

export default LessonMaterials;
